import React, { useState, useCallback, useMemo } from "react";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { useDateRangeSelector, setLoading } from "../../../redux/actions/app";
import { useDispatch } from "react-redux";
import Axios from "axios";
import CONFIG from "../../../model/TSConfig";
import { tt } from "../../../utils/i18n/i18n";
import moment from "moment-timezone";
import { saveAs } from "file-saver";
import { PaginatedTable } from "./PaginatedTable";

const PAGE_SIZE = 20;

interface FormDataEntry {
  ts: number;
  data: Record<string, any>;
}

export const FormDataTable = () => {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const dateRange = useDateRangeSelector();
  const [totalCount, setTotalCount] = useState(0);
  const [formDataEntries, setFormDataEntries] = useState<FormDataEntry[]>([]);

  const dataKeys = useMemo(() => {
    const keys = new Set<string>();
    formDataEntries.forEach((entry) => {
      if (entry.data) {
        Object.keys(entry.data).forEach((key) => keys.add(key));
      }
    });
    return Array.from(keys).sort();
  }, [formDataEntries]);

  const fetchCount = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/form_data/count`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        }
      });

      if (response.data?.payload) {
        setTotalCount(response.data.payload);
      }
    } catch (error) {
      console.error("Error fetching form data count:", error);
      setTotalCount(0);
    }
  }, [accountToDisplay, dateRange]);

  const fetchFormData = useCallback(
    async (pageIndex: number) => {
      if (!accountToDisplay?.trackingID || !dateRange) {
        return;
      }

      dispatch(setLoading(true));

      try {
        const response = await Axios({
          method: "get",
          url: `${CONFIG.jetpackBackendUrl}/custdash/form_data`,
          params: {
            owner: accountToDisplay?.trackingID,
            sts: moment(dateRange.start).unix(),
            ets: moment(dateRange.end).unix(),
            pageIndex,
            pageSize: PAGE_SIZE
          }
        });

        if (response.data?.payload) {
          setFormDataEntries(response.data.payload);
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        setFormDataEntries([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [accountToDisplay, dateRange, dispatch]
  );

  const handleDownload = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/form_data/csv`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        },
        responseType: "blob"
      });

      const startDate = moment(dateRange.start).format("YYYY-MM-DD");
      const endDate = moment(dateRange.end).format("YYYY-MM-DD");
      const fileName = `form_data_${accountToDisplay?.placeName}_${startDate}_${endDate}.csv`;

      saveAs(response.data, fileName);
    } catch (error) {
      console.error("Error downloading form data CSV:", error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [accountToDisplay, dateRange, dispatch]);

  const renderTableHeaders = useCallback(() => {
    return (
      <tr>
        <th>{tt("date_and_hour")}</th>
        {dataKeys.map((key) => (
          <th key={key}>{key}</th>
        ))}
      </tr>
    );
  }, [dataKeys]);

  const renderTableRow = useCallback(
    (entry: FormDataEntry, index: number) => {
      return (
        <tr key={`form-data-${index}`}>
          <td>{moment(entry.ts * 1000).format(tt("date_format_full_date"))}</td>
          {dataKeys.map((key) => (
            <td key={key}>
              {entry.data?.[key] !== undefined ? String(entry.data[key]) : "-"}
            </td>
          ))}
        </tr>
      );
    },
    [dataKeys]
  );

  if (!accountToDisplay?.trackingID) {
    return null;
  }

  return (
    <div className="form-dtotalCountata-container">
      <PaginatedTable<FormDataEntry>
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        data={formDataEntries}
        onFetchCount={fetchCount}
        onFetchData={fetchFormData}
        onDownload={handleDownload}
        renderHeaders={renderTableHeaders}
        renderRow={renderTableRow}
        keyPrefix="form-data"
        showDownloadButton={true}
        trackingID={accountToDisplay.trackingID}
      />
    </div>
  );
};
